import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material'
import { Theme, useTheme } from '@mui/material/styles'
import { useNavigate } from 'react-router-dom'
import {
  ConfirmIcon,
  ExpandIcon,
  InfoIcon,
  PlayArrowIcon,
  RightArrowIcon,
  StudentIcon,
} from 'src/v3/core/icons'
import {
  CourseV2Accreditation,
  CourseV2Content,
} from 'src/v3/classroom-v2/types'
import { NotYetAvailable } from 'src/v3/classroom-v2/components/not-yet-available'
import { AccreditationReadinessCard } from 'src/v3/classroom-v2/components/accreditation-readiness'
import { CourseCompletionPanel } from 'src/v3/classroom-v2/components/course-completion-panel'
import { AccreditationStatements } from 'src/v3/classroom-v2/components/accreditation-statements'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  explorationEntryRequested,
  selectIsCourseFullyComplete,
} from 'src/v3/classroom-v2/classroom-v2-wizard.slice'
import { getInitials } from 'src/v3/utilities/initials'

let ceBrokerStateAbbreviation: Record<string, string> = {
  Florida: 'FL',
  Kentucky: 'KY',
  Tennessee: 'TN',
}

type AccreditationColorKey = keyof Theme['palette']['accreditationColors']

type AccreditationRowInfo = {
  badgeLabel: string
  colorKey: AccreditationColorKey
  // null when the badge already says everything the name would — CAPCE,
  // ANCC, Accredited and Not Accredited all have a badgeLabel identical to
  // their name, so printing both is the row saying the same word twice.
  label: string | null
  detail: string | null
}

let accreditationRowInfoByKind: {
  [K in CourseV2Accreditation['kind']]: (
    accreditation: Extract<CourseV2Accreditation, { kind: K }>,
  ) => AccreditationRowInfo
} = {
  capce: (accreditation) => ({
    badgeLabel: 'CAPCE',
    colorKey: 'capce',
    label: null,
    detail:
      [accreditation.classType, accreditation.topicArea]
        .filter(Boolean)
        .join(' · ') || null,
  }),
  ancc: () => ({
    badgeLabel: 'ANCC',
    colorKey: 'ancc',
    label: null,
    detail: null,
  }),
  ceBroker: (accreditation) => ({
    badgeLabel:
      ceBrokerStateAbbreviation[accreditation.state] ??
      accreditation.state.slice(0, 2).toUpperCase(),
    colorKey: 'ceBroker',
    label: `${accreditation.state} (CE Broker)`,
    detail: null,
  }),
  virginia: (accreditation) => ({
    badgeLabel: 'VA',
    colorKey: 'virginia',
    label: 'Virginia',
    detail:
      [
        accreditation.areaNumberALS ? 'ALS' : null,
        accreditation.areaNumberBLS ? 'BLS' : null,
      ]
        .filter(Boolean)
        .join(' · ') || null,
  }),
  newJersey: () => ({
    badgeLabel: 'NJ',
    colorKey: 'newJersey',
    label: 'New Jersey',
    detail: null,
  }),
  newYork: (accreditation) => ({
    badgeLabel: 'NY',
    colorKey: 'newYork',
    label: 'New York',
    detail: accreditation.topicArea || null,
  }),
  accredited: (accreditation) => ({
    badgeLabel: 'Accredited',
    colorKey: 'accredited',
    label: null,
    detail: accreditation.courseCode || null,
  }),
  notAccredited: (accreditation) => ({
    badgeLabel: 'Not Accredited',
    colorKey: 'notAccredited',
    label: null,
    detail: accreditation.courseCode || null,
  }),
}

let accreditationRowInfo = (
  accreditation: CourseV2Accreditation,
): AccreditationRowInfo =>
  accreditationRowInfoByKind[accreditation.kind](accreditation as never)

let AccreditationBadge = ({
  label,
  color,
}: {
  label: string
  color: string
}) => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      px: 1,
      py: 0.5,
      borderRadius: 1,
      bgcolor: alpha(color, 0.16),
      color,
      fontSize: '0.7rem',
      fontWeight: 700,
      letterSpacing: 0.5,
      textTransform: 'uppercase',
      whiteSpace: 'nowrap',
    }}
  >
    {label}
  </Box>
)

let AccreditationRow = ({
  accreditation,
}: {
  accreditation: CourseV2Accreditation
}) => {
  let theme = useTheme()
  let row = accreditationRowInfo(accreditation)
  let color = theme.palette.accreditationColors[row.colorKey]

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        py: 1.5,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <AccreditationBadge label={row.badgeLabel} color={color} />
        {row.label && <Typography variant="body1">{row.label}</Typography>}
      </Box>
      {row.detail ? (
        <Chip label={row.detail} variant="outlined" size="small" />
      ) : null}
    </Box>
  )
}

let accreditationTargetAudience = (accreditation: CourseV2Accreditation) =>
  accreditation.kind === 'ancc' ? accreditation.targetAudience : []

export let formatHours = (length: number) =>
  Number.isInteger(length) ? length.toFixed(1) : String(length)

let hoursLabel = (length: number) => (length === 1 ? 'Hour' : 'Hours')

let SectionLabel = ({
  children,
  tooltip,
}: {
  children: string
  // Not every card needs one — the tooltip only earns its place where the
  // heading alone could be read as a stronger claim than the card actually
  // makes (see the accreditations card, which lists what the course CAN
  // qualify for, not what this learner's hours WILL be submitted to).
  tooltip?: string
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
    <Typography
      component="h2"
      color="text.secondary"
      variant="overline"
      // overline's theme line-height (2.66) makes the text's box much taller
      // than the icon beside it — centering the two BOXES still leaves the
      // glyphs looking off from the icon, since the icon has no comparable
      // padding of its own. Tightened to 1 so what's centered is the glyphs.
      sx={{ letterSpacing: 1, lineHeight: 1 }}
    >
      {children}
    </Typography>
    {tooltip && (
      // A bare InfoIcon is an SVG, not a focusable element, so a keyboard user
      // could never trigger this tooltip and a screen reader would skip it
      // entirely. IconButton gives it a real, focusable accessible name — the
      // same pattern used for the equivalent info tooltip in
      // profile/v4/certificates/index.tsx.
      <Tooltip title={tooltip}>
        <IconButton size="small" aria-label={tooltip} sx={{ p: 0 }}>
          <InfoIcon
            fontSize="inherit"
            sx={{ color: 'text.disabled', fontSize: '1rem' }}
          />
        </IconButton>
      </Tooltip>
    )}
  </Box>
)

export let CardHeading = ({
  children,
  icon,
  noMargin,
}: {
  children: string
  icon?: React.ReactNode
  noMargin?: boolean
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      width: '100%',
      mb: noMargin ? 0 : 2,
    }}
  >
    {icon}
    <Typography
      component="h2"
      color="text.secondary"
      variant="overline"
      sx={{ letterSpacing: 1, whiteSpace: 'nowrap' }}
    >
      {children}
    </Typography>
    <Divider sx={{ flex: 1 }} />
  </Box>
)

export let CourseOverview = ({ content }: { content: CourseV2Content }) => {
  let navigate = useNavigate()
  let dispatch = useAppDispatch()
  // Only to relabel the hero's call to action. Everything the completion state
  // actually SAYS lives in CourseCompletionPanel, which reads this itself — the
  // panel isn't handed a flag from here, so the two can't disagree about whether
  // this learner is done.
  let isComplete = useAppSelector(selectIsCourseFullyComplete(content.courseID))
  let capceAccreditation = content.accreditation.find(
    (a): a is Extract<CourseV2Accreditation, { kind: 'capce' }> =>
      a.kind === 'capce',
  )
  let firstStepIndex = content.steps.length > 0 ? 0 : null

  return (
    <Box>
      {/* Hero: banner + name + length. No categories/description-blurb/CTA
        state in the contract yet, so those stay as explicit placeholders
        rather than being invented from nothing. */}
      <Box
        sx={{
          position: 'relative',
          height: 320,
          borderRadius: 0,
          overflow: 'hidden',
          backgroundImage: content.banner
            ? `url(${import.meta.env.VITE_CLOUDFRONT_URL}/images/${content.banner.replace('originals', 'resized')})`
            : `url(${import.meta.env.VITE_CLOUDFRONT_URL}/public/${content.thumbnail})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(transparent, rgba(0,0,0,0.6), rgba(0,0,0,0.95))',
          }}
        />
        <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, p: 4 }}>
          <Typography
            variant="h1"
            component="h1"
            sx={{ color: 'white', mb: 1, fontSize: '2rem', lineHeight: 1.25 }}
          >
            {content.name}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<PlayArrowIcon />}
              disabled={firstStepIndex === null}
              onClick={() => {
                if (firstStepIndex === null) return
                // A finished course reopens in exploration mode — nothing is
                // left to earn, so nothing there should look like it's being
                // recorded again. Announced here rather than dispatching
                // setExplorationMode directly: the player's own mount effect
                // (resetWizard) always wipes explorationModeByCourseID back to
                // the compliance default, so setting the flag from this page
                // would just be undone the instant the player mounted. This
                // one-shot request is consumed BY that same resetWizard, so
                // there's no window where the reset and the request could
                // disagree about which one wins.
                if (isComplete) {
                  dispatch(explorationEntryRequested(content.courseID))
                }
                navigate(`/classroom-v2/${content.courseID}/player`)
              }}
            >
              {/* "Review" rather than "Start" once the course is finished, because
                  the player genuinely won't start anything: the phase gate routes a
                  completed course to its certificate and the steps open in review
                  mode. A button labelled Start Course that reviews instead is the
                  page misdescribing what it does. Restarting — the thing that DOES
                  start it over — is a separate, explained affordance in the
                  completion panel below, not a relabelled version of this. */}
              {isComplete ? 'Review Course' : 'Start Course'}
            </Button>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <Chip
              label={content.category}
              variant="outlined"
              size="small"
              sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.4)' }}
            />
            {capceAccreditation ? (
              <Chip
                label={capceAccreditation.topicArea}
                variant="outlined"
                size="small"
                sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.4)' }}
              />
            ) : null}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          px: 3,
          py: 4,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' },
          // auto then 1fr: when the sidebar is the taller side, its extra
          // height lands below the statements rather than opening a gap
          // between References and them.
          gridTemplateRows: { lg: 'auto 1fr' },
          gap: 4,
        }}
      >
        {/* LEFT column */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            alignSelf: 'start',
            gridColumn: { lg: 1 },
            gridRow: { lg: 1 },
          }}
        >
          {/* Above the syllabus, in the main column rather than the sidebar: for a
              learner who has finished, "you're done, here's your certificate" is
              the most important thing on the page, and the description they read
              before enrolling is the least. Renders nothing when the course isn't
              complete, so the ordinary overview is unchanged. */}
          <CourseCompletionPanel content={content} />

          <Card variant="outlined" sx={{ p: 3 }}>
            <CardHeading>Course Overview</CardHeading>
            {content.syllabus?.description ? (
              <Typography variant="body1" color="text.secondary">
                {content.syllabus.description}
              </Typography>
            ) : (
              <NotYetAvailable label="Course overview description" />
            )}
          </Card>

          <Card variant="outlined" sx={{ p: 3 }}>
            <CardHeading
              icon={<StudentIcon fontSize="small" color="disabled" />}
            >
              Learning Objectives
            </CardHeading>
            {content.syllabus?.objectives?.length ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {content.syllabus.objectives.map((objective, index) => (
                  <Box
                    key={index}
                    sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        bgcolor: 'success.main',
                        color: 'success.contrastText',
                        mt: 0.25,
                      }}
                    >
                      <ConfirmIcon sx={{ fontSize: 14 }} />
                    </Box>
                    <Typography variant="body1">{objective}</Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              <NotYetAvailable label="Learning objectives" />
            )}
          </Card>

          <Accordion
            variant="outlined"
            disableGutters
            sx={{
              bgcolor: 'background.paper',
              borderRadius: 1,
              overflow: 'hidden',
            }}
          >
            <AccordionSummary expandIcon={<ExpandIcon />} sx={{ px: 3 }}>
              <CardHeading noMargin>References</CardHeading>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 3, pt: 0 }}>
              {content.syllabus?.references?.length ? (
                <Box
                  sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
                >
                  {content.syllabus.references.map((reference, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1.5,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          width: 20,
                          height: 20,
                          borderRadius: '50%',
                          bgcolor: 'primary.main',
                          color: 'primary.contrastText',
                        }}
                      >
                        <RightArrowIcon sx={{ fontSize: 14 }} />
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {reference}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ) : (
                <NotYetAvailable label="References" />
              )}
            </AccordionDetails>
          </Accordion>
        </Box>

        {/* RIGHT column */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            gridColumn: { lg: 2 },
            gridRow: { lg: '1 / span 2' },
          }}
        >
          <Card sx={{ p: 3 }}>
            <Typography variant="h2" component="p" sx={{ mb: 1 }}>
              {formatHours(content.length)}
            </Typography>
            <Typography variant="body2" color="text.secondary" component="h2">
              Continuing Education {hoursLabel(content.length)}
            </Typography>
          </Card>

          <AccreditationReadinessCard courseID={content.courseID} />

          {content.instructors.length ? (
            <Card sx={{ p: 3 }}>
              <SectionLabel>
                {content.instructors.length === 1
                  ? 'Instructor'
                  : 'Instructors'}
              </SectionLabel>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {content.instructors.map((instructor) => (
                  <Box
                    key={instructor.id}
                    sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
                  >
                    <Avatar sx={{ bgcolor: 'primary.dark' }}>
                      {getInitials(instructor.name)}
                    </Avatar>
                    <Box>
                      <Typography variant="body1">{instructor.name}</Typography>
                      {instructor.credentials ? (
                        <Typography
                          variant="body2"
                          sx={{ color: 'primary.light' }}
                        >
                          {instructor.credentials}
                        </Typography>
                      ) : null}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Card>
          ) : null}

          {content.accreditation.length ? (
            <Card sx={{ p: 3 }}>
              <SectionLabel>Target Audience</SectionLabel>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {content.accreditation
                  .flatMap(accreditationTargetAudience)
                  .filter(
                    (audience, index, all) => all.indexOf(audience) === index,
                  )
                  .map((audience) => (
                    <Chip key={audience} label={audience} variant="outlined" />
                  ))}
              </Box>
              {content.accreditation.every(
                (a) => accreditationTargetAudience(a).length === 0,
              ) && (
                <Typography variant="body2" color="text.disabled">
                  No target audience specified
                </Typography>
              )}
            </Card>
          ) : null}

          {content.accreditation.length ? (
            <Card sx={{ p: 3 }}>
              <SectionLabel tooltip="Your hours may not be submitted to every organization listed here. Where they'll actually be submitted is shown above, in “Your hours will be automatically submitted.”">
                Course Accreditations
              </SectionLabel>
              <Box>
                {content.accreditation.map((accreditation, index) => (
                  <Box key={index}>
                    <AccreditationRow accreditation={accreditation} />
                    {index < content.accreditation.length - 1 ? (
                      <Divider />
                    ) : null}
                  </Box>
                ))}
              </Box>
            </Card>
          ) : null}
        </Box>

        {/* After the sidebar in source order so it's last on single-column
            widths; placed back under References on wide ones. */}
        {content.accreditation.some(
          (a) => a.kind === 'capce' || a.kind === 'ancc',
        ) ? (
          <Box
            sx={{
              gridColumn: { lg: 1 },
              gridRow: { lg: 2 },
              alignSelf: 'start',
            }}
          >
            <AccreditationStatements content={content} />
          </Box>
        ) : null}
      </Box>
    </Box>
  )
}
